/**
 * PartnerGuard — lets a signed-in printer, FC or warehouse login into its portal.
 *
 * Who the partner is comes from the API (`GET /api/<kind>-portal/me`), never a
 * role read in the browser. Like the other portal guards it only ever
 * REDIRECTS — it never signs out, because the portals share one session.
 *
 * - Staff, client or another partner kind (access level is not this partner's):
 *   their own home at once (`ownHomeFromPortal`), without calling /me.
 * - A partner login the API refuses (not linked, or the partner is inactive):
 *   the not-linked message with Sign out. Redirecting would loop, because
 *   `PartnerFence` sends every other path straight back here.
 */
import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import {
  notLinkedMessage,
  ownHomeFromPortal,
  partnerForAccessLevel,
  type FlowPartnerKind,
} from "@/lib/auth/partnerAccess";
/* Deep imports on purpose: the guard is eager, and the portal index pulls in every page. */
import { errorCode } from "@/modules/SupplierPortal/api/portalErrors.ts";
import { usePartnerSession } from "@/modules/PartnerPortal/api/usePartnerPortalQueries.ts";

import { PartnerSessionContext } from "./partnerSessionContext";
import { PortalMessage } from "./PortalMessage";

const Spinner = () => (
  <div className="flex min-h-screen items-center justify-center bg-background">
    <Loader2 className="h-8 w-8 animate-spin text-primary" aria-label="Loading" />
  </div>
);

export const PartnerGuard = ({ partner, children }: { partner: FlowPartnerKind; children: ReactNode }) => {
  const { user, loading, logout } = useAuth();
  const location = useLocation();
  const isThisPartner = Boolean(user) && partnerForAccessLevel(user?.access_level) === partner;
  /* /me only for this partner's logins; everyone else is redirected below without a call. */
  const session = usePartnerSession(partner, isThisPartner ? user?.id : undefined);

  if (loading) return <Spinner />;

  if (!user) {
    /* Straight to /signin: the legacy /auth redirect would drop `state`, and with it the deep link. */
    return <Navigate to="/signin" replace state={{ from: location.pathname + location.search + location.hash }} />;
  }

  /* Not this partner's account: to its own home, never a sign-out. */
  if (!isThisPartner) return <Navigate to={ownHomeFromPortal(user.access_level)} replace />;

  const signOut = (
    <Button variant="outline" onClick={() => void logout()}>
      Sign out
    </Button>
  );

  if (session.isPending) return <Spinner />;

  if (session.isError) {
    const code = errorCode(session.error);
    const refused = code === "FORBIDDEN" || code === "NOT_FOUND";
    if (refused) {
      return (
        <PortalMessage title={notLinkedMessage(partner)} action={signOut}>
          Ask your Habits365Greek contact to link it, then sign in again.
        </PortalMessage>
      );
    }
    return (
      <PortalMessage
        title="We could not open your portal."
        action={
          <Button variant="outline" onClick={() => void session.refetch()}>
            Try Again
          </Button>
        }
      >
        Refresh the page, or contact your Habits365Greek contact.
      </PortalMessage>
    );
  }

  return <PartnerSessionContext.Provider value={session.data}>{children}</PartnerSessionContext.Provider>;
};

export default PartnerGuard;
